import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { Breadcrumbs } from '../../components/shared/Breadcrumbs'

export default function AzureSqlManagedInstancePricingUkGuide() {
  return (
    <>
      <Helmet>
        <title>Azure SQL Managed Instance Pricing UK 2026 — AHB, Reserved Instances, and Tier Selection | AzureCalc.uk</title>
        <meta
          name="description"
          content="Azure SQL Managed Instance pricing explained for UK teams. GP vs BC tier costs, Azure Hybrid Benefit savings, 1-year and 3-year Reserved Instances, included storage, and the minimum cost trap. All in GBP for UK South."
        />
        <link rel="canonical" href="https://www.azure-calc.co.uk/guides/azure-sql-managed-instance-pricing-uk" />
        <meta property="og:title" content="Azure SQL Managed Instance Pricing UK 2026 — AHB, Reserved Instances, and Tier Selection" />
        <meta property="og:url" content="https://www.azure-calc.co.uk/guides/azure-sql-managed-instance-pricing-uk" />
        <meta property="og:image" content="https://www.azure-calc.co.uk/og/azure-calc-og.png" />
        <meta property="og:type" content="article" />
      </Helmet>

      <div className="max-w-3xl mx-auto px-6 py-10">
        <Breadcrumbs />

        <h1 className="text-2xl font-bold tracking-tight mt-4 mb-3" style={{ color: 'var(--text-primary)' }}>
          Azure SQL Managed Instance Pricing UK 2026
        </h1>
        <p className="text-[14px] mb-2" style={{ color: 'var(--text-tertiary)' }}>
          General Purpose vs Business Critical · AHB · Reserved Instances · Storage · Migration planning
        </p>
        <p className="text-[12px] mb-8" style={{ color: 'var(--text-tertiary)' }}>
          All prices in GBP · UK South · Last verified April 2026
        </p>

        {/* Section 1 */}
        <h2 className="text-[18px] font-bold mt-8 mb-3" style={{ color: 'var(--text-primary)' }}>
          What SQL Managed Instance actually costs
        </h2>
        <p className="text-[14px] mb-3" style={{ color: 'var(--text-secondary)' }}>
          SQL Managed Instance (SQL MI) is Microsoft's PaaS offering with near-100% SQL Server compatibility. Unlike Azure SQL Database, it is a full instance — not a single database. The billing model reflects that: there is no free tier, no Basic tier, and no serverless option. The minimum monthly cost starts at around £418/month (GP 4 vCore, PAYG).
        </p>
        <p className="text-[14px] mb-3" style={{ color: 'var(--text-secondary)' }}>
          Three components make up the monthly bill:
        </p>
        <ul className="text-[14px] mb-4 space-y-1 list-disc list-inside" style={{ color: 'var(--text-secondary)' }}>
          <li><strong>Compute</strong> — priced per vCore per hour, split by tier (GP vs BC)</li>
          <li><strong>Storage</strong> — provisioned storage above the included amount per vCore</li>
          <li><strong>Backup</strong> — PITR backup storage, charged by redundancy type</li>
        </ul>

        {/* Section 2 */}
        <h2 className="text-[18px] font-bold mt-8 mb-3" style={{ color: 'var(--text-primary)' }}>
          General Purpose vs Business Critical
        </h2>
        <p className="text-[14px] mb-3" style={{ color: 'var(--text-secondary)' }}>
          The two production tiers in SQL MI are General Purpose (GP) and Business Critical (BC). The compute rates for UK South (GBP, April 2026):
        </p>
        <div className="overflow-x-auto mb-4">
          <table className="w-full text-[13px]">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--outline-variant)' }}>
                <th className="text-left py-2 pr-6 font-semibold" style={{ color: 'var(--text-secondary)' }}>Tier</th>
                <th className="text-left py-2 pr-6 font-semibold" style={{ color: 'var(--text-secondary)' }}>Per vCore/hr (PAYG)</th>
                <th className="text-left py-2 pr-6 font-semibold" style={{ color: 'var(--text-secondary)' }}>8 vCore/month</th>
                <th className="text-left py-2 font-semibold" style={{ color: 'var(--text-secondary)' }}>8 vCore/month (AHB+3yr)</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['General Purpose', '£0.1432', '£836/month', '~£167/month'],
                ['Business Critical', '£0.2863', '£1,672/month', '~£375/month'],
              ].map(([tier, rate, payg, opt]) => (
                <tr key={tier} style={{ borderBottom: '1px solid var(--outline-variant)' }}>
                  <td className="py-2 pr-6 font-medium" style={{ color: 'var(--text-primary)' }}>{tier}</td>
                  <td className="py-2 pr-6" style={{ color: 'var(--text-secondary)' }}>{rate}</td>
                  <td className="py-2 pr-6" style={{ color: '#a4262c' }}>{payg}</td>
                  <td className="py-2" style={{ color: '#107c10' }}>{opt}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[14px] mb-3" style={{ color: 'var(--text-secondary)' }}>
          BC costs approximately twice as much as GP for the same vCore count. The premium buys: local SSD storage (vs remote GP), built-in read replicas (three copies), higher IOPS and throughput, and faster failover (&lt;1 second vs 30 seconds for GP).
        </p>
        <p className="text-[14px] mb-3" style={{ color: 'var(--text-secondary)' }}>
          For most lift-and-shift migrations, GP is the right starting tier. Move to BC only if you have specific high-IOPS, sub-second failover, or read-scale requirements.
        </p>

        {/* Section 3 */}
        <h2 className="text-[18px] font-bold mt-8 mb-3" style={{ color: 'var(--text-primary)' }}>
          Azure Hybrid Benefit — the biggest lever
        </h2>
        <p className="text-[14px] mb-3" style={{ color: 'var(--text-secondary)' }}>
          Azure Hybrid Benefit (AHB) lets you apply existing SQL Server licences (with active Software Assurance) to reduce the compute cost of SQL MI. The savings are significant because a large portion of the PAYG rate is the SQL Server licence fee itself.
        </p>
        <ul className="text-[14px] mb-4 space-y-1 list-disc list-inside" style={{ color: 'var(--text-secondary)' }}>
          <li><strong>GP AHB:</strong> ~30% compute saving (removes Standard Edition licence cost)</li>
          <li><strong>BC AHB:</strong> ~50% compute saving (removes Enterprise Edition licence cost — higher value)</li>
        </ul>
        <p className="text-[14px] mb-3" style={{ color: 'var(--text-secondary)' }}>
          If you are migrating from on-premises SQL Server with active SA, AHB should be the first optimisation you apply. The licence cost you are already paying is removed from the cloud bill entirely.
        </p>

        {/* Section 4 */}
        <h2 className="text-[18px] font-bold mt-8 mb-3" style={{ color: 'var(--text-primary)' }}>
          Reserved Instances — stacking discounts
        </h2>
        <p className="text-[14px] mb-3" style={{ color: 'var(--text-secondary)' }}>
          SQL MI supports 1-year and 3-year Reserved Instance commitments on the compute layer. RI discounts apply on top of any AHB savings — the discounts stack multiplicatively, not additively.
        </p>
        <div className="overflow-x-auto mb-4">
          <table className="w-full text-[13px]">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--outline-variant)' }}>
                <th className="text-left py-2 pr-6 font-semibold" style={{ color: 'var(--text-secondary)' }}>Scenario</th>
                <th className="text-left py-2 pr-6 font-semibold" style={{ color: 'var(--text-secondary)' }}>GP 8 vCore/month</th>
                <th className="text-left py-2 font-semibold" style={{ color: 'var(--text-secondary)' }}>BC 8 vCore/month</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['PAYG', '£836', '£1,672'],
                ['PAYG + AHB', '£585', '£836'],
                ['PAYG + 1yr RI', '£560', '£1,120'],
                ['AHB + 1yr RI', '£392', '£560'],
                ['AHB + 3yr RI', '£263', '£376'],
              ].map(([scenario, gp, bc]) => (
                <tr key={scenario} style={{ borderBottom: '1px solid var(--outline-variant)' }}>
                  <td className="py-2 pr-6 font-medium" style={{ color: 'var(--text-primary)' }}>{scenario}</td>
                  <td className="py-2 pr-6" style={{ color: 'var(--text-secondary)' }}>{gp}</td>
                  <td className="py-2" style={{ color: 'var(--text-secondary)' }}>{bc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[14px] mb-3" style={{ color: 'var(--text-secondary)' }}>
          Important: RI discounts apply to compute only. Storage, backup, and other charges are not reduced by a Reserved Instance.
        </p>

        {/* Section 5 */}
        <h2 className="text-[18px] font-bold mt-8 mb-3" style={{ color: 'var(--text-primary)' }}>
          Storage — included and extra
        </h2>
        <p className="text-[14px] mb-3" style={{ color: 'var(--text-secondary)' }}>
          SQL MI includes 32 GB of storage per vCore at no extra charge. For an 8-vCore instance, that is 256 GB included. Storage above the included amount is charged monthly:
        </p>
        <ul className="text-[14px] mb-4 space-y-1 list-disc list-inside" style={{ color: 'var(--text-secondary)' }}>
          <li><strong>GP extra storage:</strong> £0.0995/GB/month</li>
          <li><strong>BC extra storage:</strong> £0.2167/GB/month</li>
        </ul>
        <p className="text-[14px] mb-3" style={{ color: 'var(--text-secondary)' }}>
          For a 1 TB database on GP 8 vCore: (1024 - 256) × £0.0995 = £76.44/month in extra storage. This is a meaningful cost at scale — factor included storage into your vCore sizing decisions.
        </p>

        {/* Section 6 */}
        <h2 className="text-[18px] font-bold mt-8 mb-3" style={{ color: 'var(--text-primary)' }}>
          SQL MI vs SQL Database — when to choose each
        </h2>
        <p className="text-[14px] mb-3" style={{ color: 'var(--text-secondary)' }}>
          SQL MI is the right choice when:
        </p>
        <ul className="text-[14px] mb-4 space-y-1 list-disc list-inside" style={{ color: 'var(--text-secondary)' }}>
          <li>You are lifting an on-premises SQL Server with minimal code changes</li>
          <li>Your application uses SQL Server Agent, cross-database queries, or instance-level features</li>
          <li>You need CLR integration, linked servers, or distributed transactions</li>
          <li>Your team cannot spend time on SQL Database compatibility testing</li>
        </ul>
        <p className="text-[14px] mb-3" style={{ color: 'var(--text-secondary)' }}>
          SQL Database is the right choice when:
        </p>
        <ul className="text-[14px] mb-4 space-y-1 list-disc list-inside" style={{ color: 'var(--text-secondary)' }}>
          <li>You are building a new cloud-native application</li>
          <li>You need a serverless option (auto-pause, scale-to-zero)</li>
          <li>Cost is the primary driver and the workload can use Basic/Standard tiers</li>
          <li>You only need a single database (not a full instance)</li>
        </ul>

        {/* Section 7: FAQ */}
        <h2 className="text-[18px] font-bold mt-10 mb-4" style={{ color: 'var(--text-primary)' }}>FAQ</h2>
        {[
          {
            q: 'What is the minimum monthly cost for SQL Managed Instance?',
            a: 'GP 4 vCore PAYG is approximately £418/month. With AHB + 3-year RI, this drops to around £131/month. There is no free or Basic tier.',
          },
          {
            q: 'Can I use AHB and a Reserved Instance at the same time?',
            a: 'Yes. AHB and RI discounts stack multiplicatively on the compute cost. AHB is applied first, then the RI discount is applied to the AHB-reduced rate.',
          },
          {
            q: 'Does SQL MI support zone redundancy?',
            a: 'Yes — both GP and BC support zone-redundant deployments at an additional charge (approximately £0.0859/vCore/hr for GP zone redundancy).',
          },
          {
            q: 'How long does an SQL MI deployment take?',
            a: 'Initial deployment typically takes 4–6 hours. Subsequent operations (scaling, failover) are faster. Plan for this in migration timelines.',
          },
          {
            q: 'Is backup storage included in the SQL MI price?',
            a: 'A certain amount of backup storage is included (roughly equivalent to your database size for LRS). Backup storage beyond that is charged at £0.0865/GB/month for LRS.',
          },
        ].map(({ q, a }) => (
          <div key={q} className="mb-5">
            <p className="text-[14px] font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>{q}</p>
            <p className="text-[14px]" style={{ color: 'var(--text-secondary)' }}>{a}</p>
          </div>
        ))}

        {/* CTA */}
        <div className="mt-10 pt-8 space-y-3" style={{ borderTop: '1px solid var(--outline-variant)' }}>
          <Link
            to="/sql-managed-instance"
            className="flex items-center gap-3 p-4 rounded transition-colors group"
            style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
          >
            <span className="material-symbols-outlined text-[#0078d4] text-[20px]" style={{ fontVariationSettings: "'FILL' 1" }}>calculate</span>
            <div>
              <p className="text-[14px] font-semibold group-hover:text-[#0078d4] transition-colors" style={{ color: 'var(--text-secondary)' }}>Azure SQL Managed Instance Calculator</p>
              <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>Model GP vs BC, AHB, and Reserved Instances in GBP</p>
            </div>
            <span className="material-symbols-outlined text-[#0078d4] text-[16px] ml-auto">arrow_forward</span>
          </Link>
          <Link
            to="/sql-database"
            className="flex items-center gap-3 p-4 rounded transition-colors group"
            style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
          >
            <span className="material-symbols-outlined text-[#0078d4] text-[20px]" style={{ fontVariationSettings: "'FILL' 1" }}>database</span>
            <div>
              <p className="text-[14px] font-semibold group-hover:text-[#0078d4] transition-colors" style={{ color: 'var(--text-secondary)' }}>Azure SQL Database Calculator</p>
              <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>Compare DTU vs vCore, Serverless, and Hyperscale tiers</p>
            </div>
            <span className="material-symbols-outlined text-[#0078d4] text-[16px] ml-auto">arrow_forward</span>
          </Link>
        </div>
      </div>
    </>
  )
}
